<template lang="pug">
.ArtistDetailHeader
	.img-wrap
		img(:src="image" width="128" height="128" :alt="`${artistName} - Karaoke`")
		SingaButton.play-button(@click="playArtistSong()" v-if="!disabledSong")
			SingaIcon(size="large" icon="play-circle")
	.detail-info-wrap
		.detail-title
			h1.title {{ artistName }}
				span.title-karaoke Karaoke
			span.title-sub {{ t('artists.artist.title') }}
		.detail-info-bottom
			.left
			.right
				CommonShareMenu
				ArtistFavoriteButton(v-if="isUserAuthorised" :id="artist.id" :resourceId="artist.resource_id" :title="artist.name" :link="favoritesLink" accessPoint="Artist detail header")
				SearchFilterInput.detail-search-input(size="is-small" :placeholderText="t('filter.title')" :filterMode="true" @filter-changed="filterChanged")
				SongListSortOptions(@sort-filter-changed="sortFilterChanged" :filter="filter" sortValue="top" :filtersToHide="hideSortFilters")
				ButtonsToggleGroup
					template(#buttons)
						SingaButton.is-small.is-rounded.is-transparent-dark(@click="userStore.setUseSongGrid(true)" icon-right="apps" :class="{ 'is-active': userStore.getUseSongGrid }")
						SingaButton.is-small.is-rounded.is-transparent-dark(@click="userStore.setUseSongGrid(false)" icon-right="list" :class="{ 'is-active': !userStore.getUseSongGrid }")
</template>

<script setup lang="ts">
import { FastAverageColor } from 'fast-average-color'
import { usePlaySong } from '~/composables/useQueue'
import type { Color, Song } from '~~/types'

const { isUserAuthorised } = useUser()
const { t } = useI18n()
const route = useRoute()

type Nullable<T> = T | null
const userStore = useUserStore()
const playSong = usePlaySong
const useImage = useGetImageSize()
const props = defineProps({
	artist: {
		required: true,
		type: Object
	},
	songs: {
		type: Array as PropType<Nullable<Song[]>>,
		required: true
	},
	filter: {
		type: String,
		default: 'top'
	}
})

const emit = defineEmits(['filterChanged', 'sortFilterChanged'])
const favoritesLink = route.fullPath

const image = useImage(props.artist.image, 'small', 'song', true)
const artistName = ref(props.artist.name)
// const activeSortDropdown = ref(false)

const hideSortFilters = ['artist_name', '-artist_name']

const filterChanged = (filterString: string) => {
	emit('filterChanged', filterString)
}

const sortFilterChanged = (value: string) => {
	emit('sortFilterChanged', value)
}

const playArtistSong = () => {
	if (props.songs && props.songs[0]) {
		playSong(props.songs[0] as Song, false, t('song.error.play'))
	}
}

const disabledSong = computed(() => {
	if (props.songs && props.songs[0]) {
		return props.songs.length === 0 || props.songs[0].geoblocked === true || props.songs[0].variants.length === 0
	}
	return false
})

onMounted(() => {
	if (props.artist.image) {
		const fac = new FastAverageColor()
		fac.getColorAsync(image).then(color =>
			useEventEmit('layoutGradient:applyColor', color.hex as Color)
		).catch(e =>
			console.error(e)
		)
	}
})

onUnmounted(() => {
	useEventEmit('layoutGradient:resetDefault')
})
</script>

<style lang="sass" scoped>
.ArtistDetailHeader
	display: flex
	flex-wrap: wrap
	flex-direction: column
	align-items: flex-start
	@media (min-width: $tablet)
		flex-direction: row
		align-items: center
.left
	display: flex

.right
	display: flex
	> *:not(:last-child)
		margin-right: $spacing-24
.img-wrap
	width: 160px
	height: 160px
	border-radius: $radius-default
	background-color: $transparent-white-4
	position: relative
	display: flex
	flex-shrink: 0
	align-self: center
	margin-bottom: $spacing-24
	box-shadow: $shadow-default
	overflow: hidden
	img
		padding: $spacing-16
		border-radius: $radius-default
		display: inline-block
		width: 100%
	@media (min-width: $tablet)
		margin-right: $spacing-32
		margin-bottom: 0
.play-button
	border-radius: $radius-round
	border-color: transparent
	display: flex
	align-items: center
	justify-content: center
	position: absolute
	bottom: 0
	left: 0
	background-color: transparent
	padding: 0
	width: 42px
	height: 42px
	line-height: 1
	margin: 0 0 12px 12px
	background-color: $transparent-black-56
	border-radius: $radius-round
	.icon
		width: 52px
		height: 52px
		margin-top: 1px
h1.title
	@include fontSize(3xl)
	@include font(basier, bold)
	margin-bottom: 0
	color: $color-grey-30
	text-overflow: ellipsis
	display: block
	white-space: nowrap
	overflow: hidden
	@media (min-width: $tablet)
		font-size: 40px
		line-height: 64px !important
.title-sub
	@include font(basier, semiBold)
	@include fontSize(s)
	color: $color-grey-60
	@media (min-width: $tablet)
		font-size: 24px
		line-height: 36px
.detail-info
	color: $transparent-white-40
.top
	display: flex
.bottom
	display: flex
.detail-info-bottom
	display: none
	@media (min-width: $tablet)
		display: flex
		justify-content: space-between
		align-items: flex-end
.detail-info-wrap
	display: flex
	flex-direction: column
	justify-content: space-between
	@media (min-width: $tablet)
		width: calc(100% - 192px)
.filter-icon
	margin-right: $spacing-8 !important

:deep(.filter-button)
	height: 48px

.title-karaoke
	@include font(basier, bold)
	color: $color-grey-70
	padding-left: $spacing-8
	letter-spacing: .4px !important
</style>
